import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link, useNavigate } from 'react-router';
import { formsApi } from '@/api/forms';
import { submissionsApi, type SubmissionFilters } from '@/api/submissions';
import { queryKeys } from '@/lib/queryKeys';
import { formatDateTime } from '@/lib/formatDate';
import { cn } from '@/lib/cn';
import { Copy, Check, Download, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';

export function FormDetailPage() {
  const { formId } = useParams<{ formId: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [copied, setCopied] = useState('');
  const [filters, setFilters] = useState<SubmissionFilters>({ page: 1, limit: 20 });

  const { data: formData } = useQuery({
    queryKey: queryKeys.forms.detail(formId!),
    queryFn: () => formsApi.get(formId!),
    enabled: !!formId,
  });

  const { data: subsData, isLoading: subsLoading } = useQuery({
    queryKey: queryKeys.submissions.list(formId!, { ...filters, page }),
    queryFn: () => submissionsApi.list(formId!, { ...filters, page }),
    enabled: !!formId,
  });

  const toggleReadMutation = useMutation({
    mutationFn: (subId: string) => submissionsApi.toggleRead(formId!, subId),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.submissions.list(formId!, { ...filters, page }) }),
  });

  const deleteFormMutation = useMutation({
    mutationFn: () => formsApi.delete(formId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.forms.all });
      navigate('/forms');
    },
  });

  const form = formData?.form;
  const submissions = subsData?.items ?? [];
  const totalPages = subsData?.totalPages ?? 1;

  if (!form) {
    return <div className="animate-pulse"><div className="h-8 bg-gray-200 rounded w-1/3 mb-4" /></div>;
  }

  const endpointUrl = `${window.location.origin}/f/${form.slug}`;
  const snippet = `<form action="${endpointUrl}" method="POST">
  <input type="text" name="name" placeholder="Seu nome" required>
  <input type="email" name="email" placeholder="Seu e-mail" required>
  <textarea name="message" placeholder="Sua mensagem"></textarea>
  <input type="text" name="${form.honeypotField}" style="display:none" tabindex="-1" autocomplete="off">
  <button type="submit">Enviar</button>
</form>`;

  function copyText(text: string, key: string) {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 1500);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{form.name}</h1>
          <span className={cn(
            'px-2 py-0.5 rounded-full text-xs font-medium',
            form.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700',
          )}>
            {form.isActive ? 'Ativo' : 'Inativo'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/forms/${form.id}/edit`}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <Pencil className="h-4 w-4" /> Editar
          </Link>
          <button
            onClick={() => { if (confirm('Excluir este formulario e todos os seus envios?')) deleteFormMutation.mutate(); }}
            disabled={deleteFormMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" /> {deleteFormMutation.isPending ? 'Excluindo...' : 'Excluir'}
          </button>
        </div>
      </div>

      {/* Endpoint + Snippet */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-sm text-gray-500 mb-2">URL do Endpoint</p>
          <div className="flex items-center gap-2">
            <code className="text-sm bg-gray-100 px-3 py-1.5 rounded flex-1 overflow-x-auto">{endpointUrl}</code>
            <button onClick={() => copyText(endpointUrl, 'url')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              {copied === 'url' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-gray-400" />}
            </button>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-500">Codigo HTML</p>
            <button onClick={() => copyText(snippet, 'snippet')}
              className="text-xs text-primary hover:underline flex items-center gap-1">
              {copied === 'snippet' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied === 'snippet' ? 'Copiado' : 'Copiar'}
            </button>
          </div>
          <pre className="text-xs bg-gray-100 p-3 rounded overflow-x-auto max-h-32">{snippet}</pre>
        </div>
      </div>

      {/* Submissions */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold">Envios ({subsData?.total ?? 0})</h2>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Buscar..."
              value={filters.search ?? ''}
              onChange={(e) => { setFilters({ ...filters, search: e.target.value }); setPage(1); }}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 w-48"
            />
            <button onClick={() => submissionsApi.exportCsv(formId!)}
              className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
              <Download className="h-3 w-3" /> CSV
            </button>
          </div>
        </div>

        {subsLoading ? (
          <div className="p-8 text-center text-gray-400">Carregando...</div>
        ) : submissions.length === 0 ? (
          <div className="p-8 text-center text-gray-400">Nenhum envio ainda.</div>
        ) : (
          <>
            <div className="divide-y divide-gray-100">
              {submissions.map((sub) => (
                <Link
                  key={sub.id}
                  to={`/forms/${formId}/submissions/${sub.id}`}
                  className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm text-gray-500">{formatDateTime(sub.metadata.submittedAt)}</span>
                      {!sub.isRead && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">Novo</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 truncate">
                      {Object.entries(sub.data || {}).slice(0, 3).map(([k, v]) => `${k}: ${String(v)}`).join(' | ')}
                    </p>
                  </div>
                  <button
                    onClick={(e) => { e.preventDefault(); toggleReadMutation.mutate(sub.id); }}
                    className="ml-4 text-xs text-gray-400 hover:text-gray-600 shrink-0"
                  >
                    {sub.isRead ? 'Marcar como nao lido' : 'Marcar como lido'}
                  </button>
                </Link>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 px-5 py-3 border-t border-gray-100">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button key={p} onClick={() => setPage(p)}
                    className={cn(
                      'px-3 py-1 rounded text-sm',
                      p === page ? 'bg-primary text-white' : 'hover:bg-gray-100',
                    )}>
                    {p}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
